import {Children, forwardRef, Ref, useMemo} from 'react';
import {useCurrentGridCols} from '../../../common/shared/hooks/useCurrentGridCols.hook';
import {getValidChildren} from '../../../common/shared/utils/getValidChildren';
import {twMerge} from '../../../common/twMerge';
import {CardViewItem} from '../../organisms/CardViewItem/CardViewItem';
import {AvailableGridCols} from './AvailableGridCols.type';
import {GridProps} from './GridProps.interface';

function _Grid(
  {
    children,
    cols = 2,
    foldableCols,
    tabletCols,
    gapPx = 1,
    gapYPx,
    separatorColor = 'transparent',
    className,
    gridItemClassName,
    showEmptyGridItem = false,
    emptyGridItem,
    ...props
  }: GridProps,
  ref: Ref<HTMLDivElement>
) {
  // 현재 화면 크기에서 실제로 적용되는 열 수를 계산
  const currentCols = useCurrentGridCols({cols, foldableCols, tabletCols});

  const colsStyle = useMemo(() => {
    if (foldableCols) {
      return [
        ONLY_MOBILE_COLS_STYLE_UNDER_FOLDABLE[cols],
        ONLY_FOLDABLE_COLS_STYLE[foldableCols],
      ];
    } else if (tabletCols) {
      return [
        ONLY_MOBILE_COLS_STYLE_UNDER_TABLET[cols],
        ONLY_TABLET_COLS_STYLE[tabletCols],
      ];
    } else {
      return NORMAL_COLS_STYLE[cols];
    }
  }, [cols, foldableCols, tabletCols]);

  const gridItems = useMemo(() => {
    const validChildren = getValidChildren(children);

    if (!showEmptyGridItem) {
      return validChildren;
    }

    const childrenCount = validChildren.length;
    const remainder = childrenCount % currentCols;

    // 빈 column이 없다면 children을 그대로 반환
    if (remainder === 0) {
      return validChildren;
    }

    // 빈 column을 채울 개수 계산
    const emptyItemsCount = currentCols - remainder;
    const emptyItems = Array.from(
      {length: emptyItemsCount},
      () => emptyGridItem ?? <CardViewItem isEmpty={true} />
    );

    return [...validChildren, ...emptyItems];
  }, [children, showEmptyGridItem, currentCols, emptyGridItem]);

  return (
    <div
      className={twMerge('w-full overflow-hidden', className)}
      {...props}
      ref={ref}
    >
      <div
        className={twMerge('grid', colsStyle)}
        style={{
          width: `calc(100% + ${gapPx}px)`,
        }}
      >
        {Children.map(gridItems, (child, idx) => (
          <div
            key={`grid_${idx}`}
            className={twMerge(gridItemClassName)}
            style={{
              /**
               * border-right에 색상을 주는 방식으로 gap 색상을 채워주는데 맨 우측 아이템의 border-right는 gap 영역이 아니므로 칠해주면 안 된다.
               * 이 때 border자체를 제거해버리면 맨 우측 아이템은 border-right 수치만큼 width가 커지게 된다.
               * 그래서 border는 놔두고 overflow hidden 요소로 Grid를 한번 더 래핑해 우측 border-right 영역을 가려주는 방식으로 구현했다.
               * XXX: (@kai) Grid는 실제로 이 속성과 grid-inner-border-cols 플러그인의 조합으로 만들어지므로, 현재는 구현부가 따로 떨어져있는 셈이다. 좋은 방법이 있을 지 찾아보면 좋을 것 같다.
               */
              borderColor: separatorColor,
              ...(gapYPx === undefined
                ? {
                    borderWidth: gapPx,
                  }
                : {
                    borderLeftWidth: gapPx,
                    borderRightWidth: gapPx,
                    borderTopWidth: gapYPx,
                    borderBottomWidth: gapYPx,
                  }),
            }}
          >
            {child}
          </div>
        ))}
      </div>
    </div>
  );
}

const NORMAL_COLS_STYLE: {[key in AvailableGridCols]: string} = {
  1: 'grid-cols-1 grid-inner-border-cols1',
  2: 'grid-cols-2 grid-inner-border-cols2',
  3: 'grid-cols-3 grid-inner-border-cols3',
  6: 'grid-cols-6 grid-inner-border-cols6',
};

// grid-inner-border 커스텀 플러그인에서 nth selctor를 사용하는데, 이 selector간에는 덮어쓰기가 안 될 수 있어서 default값을 사용하지 않고 foldable 해상도 아래일 때에만 스타일을 부여하도록 명시해 주었다.
// ex) default로 nth-child(2n), foldable에서 nth-child(3n) 에 스타일을 부여하면 foldable에서는 두 css 모두 영향을 받음
const ONLY_MOBILE_COLS_STYLE_UNDER_FOLDABLE: {
  [key in AvailableGridCols]: string;
} = {
  1: 'under-foldable:grid-cols-1 under-foldable:grid-inner-border-cols1',
  2: 'under-foldable:grid-cols-2 under-foldable:grid-inner-border-cols2',
  3: 'under-foldable:grid-cols-3 under-foldable:grid-inner-border-cols3',
  6: 'under-foldable:grid-cols-6 under-foldable:grid-inner-border-cols6',
};

const ONLY_FOLDABLE_COLS_STYLE: {[key in AvailableGridCols]: string} = {
  1: 'foldable:grid-cols-1 foldable:grid-inner-border-cols1',
  2: 'foldable:grid-cols-2 foldable:grid-inner-border-cols2',
  3: 'foldable:grid-cols-3 foldable:grid-inner-border-cols3',
  6: 'foldable:grid-cols-6 foldable:grid-inner-border-cols6',
};

const ONLY_MOBILE_COLS_STYLE_UNDER_TABLET: {
  [key in AvailableGridCols]: string;
} = {
  1: 'under-tablet:grid-cols-1 under-tablet:grid-inner-border-cols1',
  2: 'under-tablet:grid-cols-2 under-tablet:grid-inner-border-cols2',
  3: 'under-tablet:grid-cols-3 under-tablet:grid-inner-border-cols3',
  6: 'under-tablet:grid-cols-6 under-tablet:grid-inner-border-cols6',
};

const ONLY_TABLET_COLS_STYLE: {[key in AvailableGridCols]: string} = {
  1: 'tablet:grid-cols-1 tablet:grid-inner-border-cols1',
  2: 'tablet:grid-cols-2 tablet:grid-inner-border-cols2',
  3: 'tablet:grid-cols-3 tablet:grid-inner-border-cols3',
  6: 'tablet:grid-cols-6 tablet:grid-inner-border-cols6',
};

export const Grid = forwardRef(_Grid);
